import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/PassengerTable.css";
import { ArrowUp, ArrowDown, Pencil, Trash2 } from "lucide-react";

export type PassengerRow = {
  id?: number;
  name: string;
  address: string;
  phone?: string;
  address_id?: number | null;
};

type Props = {
  title?: string;                      // opcional: título
  rows: PassengerRow[];
  onRemove?: (index: number) => void;  // botón eliminar
  onMove?: (from: number, to: number) => void; // botones de orden
  /** Edición rápida inline (nombre/teléfono/dirección de la fila). */
  onQuickEdit?: (index: number, row: PassengerRow) => void;
  /** Abre el modal de edición completo del módulo pasajeros. */
  onEditPassenger?: (row: PassengerRow, index: number) => void;
  forceEditIndex?: number | null;
  passengers: any[];
};

function formatAddressCell(address: string) {
  if (address === "MANUAL") return "Manual";
  return address;
}

const PassengerServicesTable: React.FC<Props> = ({
  title,
  rows,
  onRemove,
  onMove,
  onQuickEdit,
  onEditPassenger,
  forceEditIndex,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const editingRow = useMemo(
    () => (editingIndex == null ? null : rows[editingIndex] ?? null),
    [editingIndex, rows]
  );

  const openEdit = (index: number) => {
    const row = rows[index];
    if (!row) return;
    setEditingIndex(index);
    setEditName(row.name ?? "");
    setEditPhone(row.phone ?? "");
    setEditAddress(row.address ?? "");
    setEditError(null);
  };

  useEffect(() => {
    if (forceEditIndex == null) return;
    if (forceEditIndex < 0 || forceEditIndex >= rows.length) return;
    openEdit(forceEditIndex);
  }, [forceEditIndex, rows.length]);

  const closeEdit = () => {
    setEditingIndex(null);
    setEditError(null);
  };

  const handleSaveEdit = () => {
    if (editingIndex == null || !editingRow) return;
    if (!editName.trim()) {
      setEditError("El nombre es obligatorio.");
      return;
    }
    if (!editAddress.trim()) {
      setEditError("La dirección es obligatoria.");
      return;
    }
    onQuickEdit?.(editingIndex, {
      ...editingRow,
      name: editName.trim(),
      phone: editPhone.trim(),
      address: editAddress.trim(),
    });
    closeEdit();
  };

  return (
    <div className="svc-table-wrap">
      {title && <div className="svc-table-title">{title}</div>}
      <table className="svc-table">
        <thead>
          <tr>
            <th>Pasajero</th>
            <th>Dirección</th>
            <th className="svc-col-seq">#</th>
            <th className="svc-col-actions">Acciones</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="svc-empty-cell">
                Sin filas agregadas
              </td>
            </tr>
          ) : (
            rows.map((r, idx) => (
              <tr key={idx}>
                <td className="svc-td-name">{r.name}</td>
                <td
                  className={`svc-td-address ${
                    r.address === "MANUAL" ? "svc-td-address--manual" : ""
                  }`}
                >
                  {formatAddressCell(r.address)}
                </td>
                <td className="svc-td-seq">{idx + 1}</td>
                <td className="svc-td-actions">
                  {/* Reordenar: solo mostrar si hay más de un pasajero */}
                  {onMove && rows.length > 1 && (
                    <>
                      <button
                        className="svc-btn-icon"
                        disabled={idx === 0}
                        onClick={() => onMove(idx, idx - 1)}
                        title="Subir"
                        aria-label="Mover arriba"
                      >
                        <ArrowUp size={18} />
                      </button>
                      <button
                        className="svc-btn-icon"
                        disabled={idx === rows.length - 1}
                        onClick={() => onMove(idx, idx + 1)}
                        title="Bajar"
                        aria-label="Mover abajo"
                      >
                        <ArrowDown size={18} />
                      </button>
                    </>
                  )}

                  {onRemove && (
                    <button
                      className="svc-btn-icon svc-btn-danger"
                      onClick={() => onRemove(idx)}
                      title="Eliminar"
                      aria-label="Eliminar pasajero"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                  {onEditPassenger ? (
                    <button
                      className="svc-btn-icon"
                      onClick={() => onEditPassenger(r, idx)}
                      title="Editar pasajero"
                      aria-label="Editar pasajero"
                      disabled={!r.id}
                    >
                      <Pencil size={18} />
                    </button>
                  ) : onQuickEdit ? (
                    <button
                      className="svc-btn-icon"
                      onClick={() => openEdit(idx)}
                      title="Editar pasajero"
                      aria-label="Editar pasajero"
                    >
                      <Pencil size={18} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow &&
        createPortal(
          <div className="svc-inline-edit-overlay" onClick={closeEdit}>
            <div
              className="svc-inline-edit-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="svc-inline-edit-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="svc-inline-edit-head">
                <h4 id="svc-inline-edit-title">Editar pasajero</h4>
                <button type="button" onClick={closeEdit} aria-label="Cerrar">
                  ×
                </button>
              </div>
              <div className="svc-inline-edit-body">
                <label>
                  Nombre
                  <input
                    value={editName}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      setEditError(null);
                    }}
                  />
                </label>
                <label>
                  Teléfono
                  <input
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                  />
                </label>
                <label>
                  Dirección
                  <input
                    value={editAddress}
                    onChange={(e) => {
                      setEditAddress(e.target.value);
                      setEditError(null);
                    }}
                  />
                </label>
                {editError && <p className="svc-inline-edit-error">{editError}</p>}
              </div>
              <div className="svc-inline-edit-actions">
                <button type="button" className="svc-inline-edit-btn svc-inline-edit-btn--ghost" onClick={closeEdit}>
                  Cancelar
                </button>
                <button type="button" className="svc-inline-edit-btn svc-inline-edit-btn--primary" onClick={handleSaveEdit}>
                  Guardar cambios
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PassengerServicesTable;
